// Senior management's answer to a rejection Legal referred to them (ADR-0014; the referral is
// RejectDialog.jsx's). Legal records a grant, naming the approver and choosing the written reply
// from the counterparty's documents; or that it was declined or withdrawn, which makes the
// rejection final.

import { useEffect, useState } from 'react';

import { listCounterpartyDocuments, transitionCounterparty } from '../../api.js';
import MoveDialog, { ReasonField } from '../../ui/MoveDialog.jsx';
import { evidenceAmong, moveRefusal } from './path.js';

// The documents a grant may cite, read when the dialog opens: the reply was attached after the
// request, perhaps a moment ago.
function useEvidence(counterparty) {
  const [documents, setDocuments] = useState(null);
  useEffect(() => {
    let cancelled = false;
    listCounterpartyDocuments(counterparty.counterparty_id)
      .then((found) => {
        if (!cancelled) setDocuments(evidenceAmong(found, counterparty.override));
      })
      .catch(() => {
        if (!cancelled) setDocuments([]);
      });
    return () => {
      cancelled = true;
    };
  }, [counterparty]);
  return documents;
}

function EvidenceField({ documents, value, onChange, optional }) {
  if (documents === null) return <p className="muted">Reading the counterparty's files…</p>;
  if (documents.length === 0) {
    return (
      <p className="hint">
        No file has been attached since the request. Attach the written reply under Files
        {optional ? ' to cite it, or record this without it.' : ' first.'}
      </p>
    );
  }
  return (
    <label>
      The written reply{optional && ' (optional)'}
      <select value={value} onChange={(event) => onChange(event.target.value)}
              required={!optional}>
        <option value="">{optional ? 'None' : 'Choose a file'}</option>
        {documents.map((document) => (
          <option key={document.document_id} value={document.document_id}>
            {document.filename}
          </option>
        ))}
      </select>
    </label>
  );
}

export function GrantOverrideDialog({ counterparty, onClose, onMoved }) {
  const approvers = counterparty.override?.approvers_emailed ?? [];
  const [approver, setApprover] = useState(approvers[0] ?? '');
  const [evidence, setEvidence] = useState('');
  const documents = useEvidence(counterparty);

  return (
    <MoveDialog
      refusal={moveRefusal}
      title={`Override granted for ${counterparty.name}`}
      hint={"Legal's rejection stays on the record; the counterparty is approved by exception, "
        + "citing the approver's written reply."}
      submitLabel="Approve by exception"
      busyLabel="Approving…"
      ready={Boolean(approver && evidence)}
      move={() => transitionCounterparty(
        counterparty.counterparty_id, 'APPROVED', counterparty.version, null,
        { approver, evidence_document_id: evidence },
      )}
      onClose={onClose}
      onMoved={onMoved}
    >
      <label>
        Granted by
        <select value={approver} onChange={(event) => setApprover(event.target.value)} required>
          {approvers.map((email) => <option key={email} value={email}>{email}</option>)}
        </select>
      </label>
      <EvidenceField documents={documents} value={evidence} onChange={setEvidence} />
    </MoveDialog>
  );
}

const ENDINGS = [
  { code: 'DECLINED', label: 'Declined by senior management' },
  { code: 'WITHDRAWN', label: 'Withdrawn' },
];

export function EndOverrideDialog({ counterparty, onClose, onMoved }) {
  const [outcome, setOutcome] = useState('DECLINED');
  const [reason, setReason] = useState('');
  const [evidence, setEvidence] = useState('');
  const documents = useEvidence(counterparty);

  return (
    <MoveDialog
      refusal={moveRefusal}
      title={`Override declined for ${counterparty.name}?`}
      hint="Legal's rejection becomes final: the counterparty is rejected."
      submitLabel="Reject counterparty"
      busyLabel="Rejecting…"
      danger
      ready={Boolean(reason.trim())}
      move={() => transitionCounterparty(
        counterparty.counterparty_id, 'REJECTED', counterparty.version, reason.trim(),
        { override_outcome: outcome, ...(evidence ? { evidence_document_id: evidence } : {}) },
      )}
      onClose={onClose}
      onMoved={onMoved}
    >
      <fieldset className="choice-set">
        <legend>The override was</legend>
        {ENDINGS.map(({ code, label }) => (
          <label key={code} className="choice">
            <input type="radio" name="override-outcome" value={code} checked={outcome === code}
                   onChange={() => setOutcome(code)} />
            {label}
          </label>
        ))}
      </fieldset>
      <ReasonField value={reason} onChange={setReason} />
      <EvidenceField documents={documents} value={evidence} onChange={setEvidence} optional />
    </MoveDialog>
  );
}
